<template>
    <LaBaseTable :headers="headers" :items="data" v-bind="$attrs">
        <template #item-email="{ item }">
            {{ item.email }}
            <LaStatusLabel v-if="item.deleted" type="error" label="deleted" />
        </template>

        <template #item-last_login="{ item }">
            <span v-if="item.last_login"> {{ formatDateTimePragueTimeZone(item.last_login) }} </span>
            <div v-else>-</div>
        </template>

        <template #item-last_invitation="{ item }">
            <span v-if="item.last_invitation"> {{ formatDateTimePragueTimeZone(item.last_invitation) }} </span>
            <span v-else>-</span>
        </template>

        <template #item-right_groups="{ item }">
            <div v-if="item.right_groups.length == 0"></div>
            <div v-else>
                <div v-if="!getExpandedState(item.id)" class="groups-container">
                    <span
                        v-for="group in getVisibleGroups(item)"
                        :key="group.publisher + group.role_code"
                        class="group-item"
                    >
                        {{ group.publisher }} - {{ group.role_code }}
                        <template v-if="group.publisher_is_yield !== null">
                            - {{ group.publisher_is_yield ? "Yield" : "AAB" }}
                        </template>
                    </span>
                    <button
                        v-if="item.right_groups.length > maxVisibleGroups"
                        @click="toggleGroupsVisibility(item.id)"
                        class="toggle-groups-btn"
                        type="button"
                    >
                        ▼ ({{ item.right_groups.length - maxVisibleGroups }})
                    </button>
                </div>
                <div v-else class="groups-expanded">
                    <div
                        v-for="group in item.right_groups"
                        :key="group.publisher + group.role_code"
                        class="group-item-expanded"
                    >
                        {{ group.publisher }} - {{ group.role_code }}
                        <template v-if="group.publisher_is_yield !== null">
                            - {{ group.publisher_is_yield ? "Yield" : "AAB" }}
                        </template>
                    </div>
                    <button @click="toggleGroupsVisibility(item.id)" class="toggle-groups-btn" type="button">
                        ▲ ({{ item.right_groups.length - maxVisibleGroups }})
                    </button>
                </div>
            </div>
        </template>

        <template #item-is_verified="{ item }">
            <div class="is-flex is-flex-direction-column">
                <div :class="{ 'red-text': !item.is_verified }">
                    {{ item.is_verified ? "Yes" : "No" }}
                </div>
                <div class="second" v-if="$mq.max.mobile" :class="{ 'red-text': !item.verified }">
                    {{ item.verified ? "Yes" : "No" }}
                </div>
            </div>
        </template>

        <template #item-actions="{ item }">
            <LaActionIcons :item="item" item-name="user" :show-delete-icon="item.id !== loggedUserId">
                <template #center-icons v-if="item.id !== loggedUserId">
                    <LaActionIcon
                        v-if="!item.is_verified"
                        type="invite"
                        title="Send an invite e-mail"
                        @click.stop="inviteUser(item)"
                    />
                </template>
            </LaActionIcons>
        </template>
    </LaBaseTable>
</template>

<script>
import { computed, ref } from "vue";
import LaActionIcons from "@/components/button/ActionIcons";
import LaActionIcon from "@/components/button/ActionIcon";
import LaBaseTable from "@/components/BaseTable";
import LaStatusLabel from "@/components/StatusLabel";
import { getterTypes } from "@/store/LoggedUser/types";
import store from "@/store/store";
import { formatDateTimePragueTimeZone } from "@/helpers/filters";

export default {
    name: "LaUserListTable",
    methods: { formatDateTimePragueTimeZone },
    components: {
        LaActionIcons,
        LaActionIcon,
        LaBaseTable,
        LaStatusLabel,
    },
    props: {
        data: {
            required: true,
        },
    },
    emits: ["sendInvitation", "removeRightGroup"],
    setup(props, { emit }) {
        const headers = computed(() => [
            {
                text: "ID",
                value: "id",
                isNumber: true,
                justifyContentCenter: true,
            },
            {
                text: "email",
                value: "email",
                direction: 1,
                flex: 2,
            },
            {
                text: "name",
                value: "name",
                flex: 2,
            },
            {
                text: "Type",
                value: "type",
            },
            {
                text: "Verified",
                value: "is_verified",
            },
            {
                text: "Right groups",
                value: "right_groups",
                flex: 3,
            },
            {
                text: "Last invitation",
                value: "last_invitation",
                sortable: false,
                isDateTime: true,
            },
            {
                text: "Last login",
                value: "last_login",
                isDateTime: true,
            },
            {
                text: "actions",
                value: "actions",
            },
        ]);
        const inviteUser = (user) => {
            if (window.confirm(`Do you really want to send an invitation to ${user.email}?`)) {
                emit("sendInvitation", user);
            }
        };
        const removeRightGroup = (user) => {
            if (window.confirm("Do you really want to remove this permission?")) {
                emit("removeRightGroup", user);
            }
        };

        const loggedUserId = computed(() => store.getters[getterTypes.getUserId]);

        const maxVisibleGroups = 1;
        const expandedRows = ref(new Set());

        const toggleGroupsVisibility = (itemId) => {
            if (expandedRows.value.has(itemId)) {
                expandedRows.value.delete(itemId);
            } else {
                expandedRows.value.add(itemId);
            }
        };

        const getExpandedState = (itemId) => {
            return expandedRows.value.has(itemId);
        };

        const getVisibleGroups = (item) => {
            if (expandedRows.value.has(item.id) || item.right_groups.length <= maxVisibleGroups) {
                return item.right_groups;
            }
            return item.right_groups.slice(0, maxVisibleGroups);
        };

        return {
            headers,
            inviteUser,
            removeRightGroup,
            loggedUserId,
            maxVisibleGroups,
            toggleGroupsVisibility,
            getExpandedState,
            getVisibleGroups,
        };
    },
    // methods: {
    //     confirmRemoveRightGroup(item) {
    //         // Zobrazíme nativní potvrzovací dialog
    //         if (window.confirm("Do you really want to remove this permission?")) {
    //             this.removeRightGroup(item);
    //         }
    //     },
    //     removeRightGroup(item) {
    //         // Sem vložte logiku pro odebrání oprávnění
    //         console.log("Oprávnění pro item", item, "bylo odebráno.");
    //     },
    // },
};
</script>

<style lang="scss" scoped>
.second {
    font-size: 0.8rem;
    opacity: 0.8;
}

.red-text {
    color: red;
}

.groups-container {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.group-item {
    display: inline;
}

.groups-expanded {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.group-item-expanded {
    display: block;
}

.toggle-groups-btn {
    --toggle-color: #8590b3;
    background: none;
    border: none;
    color: var(--toggle-color);
    cursor: pointer;
    font-size: 0.85rem;
    text-decoration: none;
    padding: 2px 4px;
    border-radius: 3px;

    &:hover {
        background-color: rgba(133, 144, 179, 0.1);
        opacity: 0.8;
    }
}
</style>
